import React, { useEffect, useState, useRef } from "react";
import {useDispatch, useSelector} from "react-redux";

import { Formik } from "formik";
import * as Yup from "yup";
import { format } from "date-fns";
import ReactDatePicker from "react-datepicker";
import { Button, Form, Col, Card, Spinner, Collapse } from "react-bootstrap";
import { Search, CaretDownFill, CaretUpFill, ClipboardData, Calendar4Week } from "react-bootstrap-icons";

import { refSagaActions } from "../../Administration/ReferenceInfoManagement";
import { agencySagaActions } from "../../Administration/AgencyManagement";
import { actions as reportsSagaActions } from "../redux/reportsSaga";
import { provinceSagaActions } from "../../Administration/ProvinceInfoManagement";
import { serviceSagaActions } from "../../Administration/ServiceManagement";
import { serviceItemSagaActions } from "../../Administration/ServiceItemManagement";

import styles from "../css/reports.module.css";

const presentDate = new Date();
const formatPresentDate = format(presentDate, "dd-MMMM-yyyy");

//search form schema definition
const reportSearchFormSchema = Yup.object().shape({
  prioritySearch: Yup.object({}),
  reportType: Yup.string().required("*Select report type"),
  dept: Yup.string(),
  serviceType: Yup.string(),
  serviceItem: Yup.string(),
  province: Yup.string(),
  interval: Yup.string()
    .required("*Select time interval"),
  fromDate: Yup.date()
    .max(presentDate, `Date must be earlier than the ${formatPresentDate}`),
  toDate: Yup.date()
    .min(Yup.ref('fromDate'), "*'Date To' must be after the 'Date From'")
    .max(presentDate, `*Date must be earlier than the ${formatPresentDate}`),
  users: Yup.string(),
  grp: Yup.string()
}, ['fromDate', 'toDate']);

const queryParamsInitialState = { 
  fromDate:"", 
  toDate:"",
  interval: "", 
  dept:"",
  serviceType: "",
  serviceItem: "",
  province: "",
  reportType:""
};


export function ReportSearchForm(props) {

  const dispatch = useDispatch();
  const searchFormRef = useRef();
  const { agency, reference, reports, province, services, serviceItem } = useSelector(state => state);

  const { agencies } = agency;
  const { allReferences, referenceKinds } = reference;

  const { listLoading } = reports;
  const { provinces } = province;
  const { serviceTypes } = services;
  const { serviceItems } = serviceItem;

  const [ reportTypes, setReportTypes ] = useState([]);
  const [ timeInteval, setTimeInteval ] = useState([]);
  const [ specificIntId, setSpecificIntId ] = useState();
  const [ interval, setInterval ] = useState("");
  const [ rcptStatuses, setRcptStatuses ] = useState([]);
  const [ showSearch, setShowSearch ] = useState(true);

  const [selectedDeptServices, setSelectedDeptServices] = useState([]);
  const [serviceItemList, setServiceItemList] = useState([]);

  useEffect(() => {
    if(referenceKinds.length > 1){

      let reportKind = referenceKinds.find((kind) => (kind.code === "REPORT_TYPES"));

      let reportTypes = allReferences.filter((ref) => (ref.kindId === reportKind.id));

      setReportTypes(reportTypes);

      let tiKind = referenceKinds.find((kind) => (kind.code === "TIME_INTERVAL"));
      let intervals = allReferences.filter((ref) => (ref.kindId === tiKind.id));
      intervals.forEach(int => {
        if(int.code === "SPECIFIC"){
          setSpecificIntId(int.id);
        }
      });
      setTimeInteval(intervals);

      let rcptKind = referenceKinds.find((kind) => (kind.code === "RECEIPT_STATUS"));
      let rcptStatuses = allReferences.filter((ref) => (ref.kindId === rcptKind.id));
      setRcptStatuses(rcptStatuses);
    }
  }, [referenceKinds, allReferences]);

  useEffect(() => {

    dispatch(refSagaActions.fetchAllReferences());
    dispatch(refSagaActions.fetchReferenceKinds());
    dispatch(agencySagaActions.fetchAgencies());
    dispatch(provinceSagaActions.fetchProvinces());
    dispatch(serviceSagaActions.fetchServices());
    dispatch(serviceItemSagaActions.fetchServiceItems());

  },[dispatch]);

  // clean up function to reset state on component unmount
  useEffect(() => {
    let searchForm = searchFormRef.current;
    return () => {
      setInterval(undefined);
      searchForm.reset();
      dispatch(reportsSagaActions.resetReportState());
    };
  }, [dispatch]);

  const filterServices = (deptId) => {
    let serviceList = serviceTypes.filter((serv) => (serv.department_id === Number(deptId)));
    setSelectedDeptServices(serviceList);
  };

  const filterServiceItems = (serviceTypeId) => {
    let serviceItemList = serviceItems.filter((item) => (item.service_id === Number(serviceTypeId)));
    setServiceItemList(serviceItemList);
  };

  const calcFromToDates = (intId) => {
    let interval = allReferences.find((ref) => (ref.id === Number(intId)));
    let today = new Date();
    let timeInt = {
      description: interval.description,
      from: "",
      to: format(today, "dd/MM/yyyy")
    };
    if(interval.description === "Daily"){

      timeInt.from = format(today, "dd/MM/yyyy");
      timeInt.to = "";

    } else if (interval.description === "Weekly"){

      timeInt.from = format(new Date(new Date(today).setDate(today.getDate() - 7)), "dd/MM/yyyy");

    } else if (interval.description === "Monthly"){

      timeInt.from = format(new Date(new Date(today).setMonth(today.getMonth() - 1)), "dd/MM/yyyy");
    }
    return timeInt;
  };

  const getReceiptStatuses = (reportName) => {
    let receiptStat;

    if (reportName === "Revenue Collections" || reportName === "Transactions"){

      receiptStat = rcptStatuses.find((rcpt) => ( rcpt.description === "Issued" )).id;
      
    } else if (reportName === "Services Rendered"){

      receiptStat = rcptStatuses.find((rcpt) => ( rcpt.description === "Used")).id;
    }
    
    return receiptStat;
  };

  const handleRequestReportReset = () => {
    setInterval(undefined);
    searchFormRef.current.reset();
    dispatch(reportsSagaActions.resetReportState());
    props.onHandleReset();
  };

  const handleRequestReport = (newQueryParams) => {
    props.onHandleReportRequest(newQueryParams);
  };

  const handleFormSubmit = (values) => {
    let reportName = allReferences.find((ref) => (ref.id === Number(values.reportType)));
    let fromToDates = values.interval ? calcFromToDates(values.interval) : values.interval;

    let newQueryParams = { 
      p_fromDate: fromToDates.description === "Specific" ? format(values.fromDate, "dd/MM/yyyy") : fromToDates.from, 
      p_toDate: fromToDates.description === "Specific" ? format(values.toDate, "dd/MM/yyyy") : fromToDates.to,
      p_timeInterval: fromToDates.description,   //check if this parameter is still in use
      p_provinceId: values.province,
      p_departmentId: values.dept,
      p_serviceItemId: values.serviceItem,
      p_serviceTypeId: values.serviceType, 
      p_reportType: reportName.description,
      p_receiptStat: getReceiptStatuses(reportName.description)
    };

    handleRequestReport(newQueryParams);
  };

  return (
    <Card>
      <Card.Header>
      <span className="row">
          <span className="col d-flex align-items-center"> 
            <h3> <ClipboardData size={26} /> {" "} Report Selection </h3> 
          </span>
          <span className="col">
            <span 
              className="p-0 m-0 d-flex justify-content-end"
            >
              { showSearch 
                ? <Button variant="light" onClick={() => {setShowSearch(false)}} > <CaretUpFill size={15} /> </Button>
                : <Button variant="light" onClick={() => {setShowSearch(true)}} > <CaretDownFill size={15} /> </Button>
              }
            </span>
          </span>
        </span>
      </Card.Header>
      <Collapse in={showSearch}>
        <Card.Body>
          <Formik
            initialValues={queryParamsInitialState}
            validationSchema={reportSearchFormSchema}
            onSubmit={(values, {setSubmitting}) => {

              setSubmitting(true);
              handleFormSubmit(values);            
              setSubmitting(false);
            }}
          >
            {({ values, errors, touched, handleBlur, handleChange, handleSubmit, isSubmitting, handleReset, setFieldValue, setFieldTouched }) => (
              
              <Form ref={searchFormRef} onSubmit={handleSubmit}>
                <Form.Row>
                  <Col lg md={12} sm={12}>
                    <Form.Group controlId="paramReportType">
                      <Form.Label>Report Type: <span className="text-danger">*</span></Form.Label>
                      <Form.Control 
                        as="select"
                        name="reportType" 
                        onChange={handleChange} 
                        onBlur={handleBlur} 
                        value={values.reportType}
                        isInvalid={touched.reportType && errors.reportType}
                        className="form-select my-1 mr-sm-2"
                        custom
                      > 
                        <option className={styles.options} value="" hidden>-- Select --</option>
                        {reportTypes.map((t, index) => (
                          <option className={styles.options} key={index} value={t.id}>
                            {t.description}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.reportType}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg md={12} sm={12}>
                    <Form.Group controlId="paramProvince">
                      <Form.Label> Province: </Form.Label>
                      <Form.Control 
                        as="select"
                        name="province"
                        onChange={handleChange} 
                        onBlur={handleBlur} 
                        value={values.province}
                        isInvalid={touched.province && errors.province}
                        className="form-select my-1 mr-sm-2"
                        custom 
                      >
                        <option className={styles.options} value="" hidden> --Select-- </option>
                        {provinces.map( (prov, idx) => (
                          <option key={idx} value={prov.id}> {prov.name} </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.province}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Form.Row>
                <Form.Row>
                  <Col lg md={12} sm={12}>
                    <Form.Group controlId="paramDepartment">
                      <Form.Label>Department:</Form.Label>
                      <Form.Control 
                        as="select"  
                        name="dept"
                        onChange={(e) => {handleChange(e); filterServices(e.target.value)}}
                        onBlur={handleBlur} 
                        value={values.dept}
                        isInvalid={touched.dept && errors.dept}
                        className="form-select my-1 mr-sm-2"
                        custom 
                      >
                        <option className={styles.options} value="" hidden>-- Select --</option>
                        {agencies.map((a, index) => (
                          <option className={styles.options} key={index} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.dept}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg md={12} sm={12}>
                    <Form.Group controlId="paramServiceType">
                      <Form.Label>Service Type:</Form.Label>
                      <Form.Control 
                        as="select"  
                        name="serviceType"
                        onChange={(e) => {handleChange(e); filterServiceItems(e.target.value); setFieldValue("serviceItem", "");}} 
                        onBlur={handleBlur} 
                        value={values.serviceType}
                        className="form-select my-1 mr-sm-2"
                        isInvalid={touched.serviceType && errors.serviceType}
                        custom
                        //disabled when a department/agency is not selected, this will reduce the length of services displayed
                        disabled={ (values.dept === undefined || values.dept === "" || values.dept === null) } 
                      >
                        <option className={styles.options} value="" hidden>-- Select --</option>
                        {selectedDeptServices.map((type, index) => (
                          <option key={index} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.serviceType}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg md={12} sm={12}>
                    <Form.Group controlId="paramServiceItem">
                      <Form.Label>Service Item:</Form.Label>
                      <Form.Control 
                        as="select" 
                        name="serviceItem"
                        onChange={handleChange} 
                        onBlur={handleBlur} 
                        value={values.serviceItem} 
                        className="form-select my-1 mr-sm-2"
                        custom
                        isInvalid={touched.serviceItem && errors.serviceItem}
                        disabled={ (values.serviceType === undefined || values.serviceType === "" || values.serviceType === null) } 
                      >
                        <option className={styles.options} value="" hidden>-- Select --</option>
                        {serviceItemList.map((i, index) => (
                          <option className={styles.options} key={index} value={i.id}>
                            {i.description}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.serviceItem}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Form.Row>
                <Form.Row>
                  <Col lg md={12} sm={12}>
                    <Form.Group controlId="paramInterval">
                      <Form.Label> Time Interval: <span className="text-danger">*</span></Form.Label>
                      <Form.Control 
                        as="select"
                        name="interval"
                        onChange={(e) => {
                          handleChange(e); 
                          setInterval(e.target.value); 
                          setFieldValue("fromDate", "");
                          setFieldValue("toDate", "");
                        }} 
                        onBlur={handleBlur} 
                        value={values.interval}
                        isInvalid={touched.interval && errors.interval}
                        className="form-select my-1 mr-sm-2"
                        custom 
                      >
                        <option className={styles.options} value="" hidden>--Select--</option>

                        { timeInteval.map((int, idx) => (
                          <option value={int.id} key={idx}> {int.description} </option>
                        )) }
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.interval}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg md={12} sm={12}>
                    <Form.Group controlId="paramFromDate">
                      <Form.Label> <Calendar4Week size={15} /> {" "} From:</Form.Label>
                      <ReactDatePicker
                        autoComplete="off"
                        className={touched.fromDate && errors.fromDate ? "form-control border border-danger" : "form-control"}
                        name="fromDate"
                        dateFormat="dd-MMMM-yyyy"
                        dateFormatCalendar="MMMM yyyy"
                        placeholderText={formatPresentDate}
                        selected={values.fromDate}
                        value={values.fromDate}
                        onChange={ (dt) => {
                          setFieldTouched("fromDate", true, true);
                          let date = dt;
                          if(date === null || date === undefined){
                            date = ""
                          }
                          let changes = { target: { name: "fromDate", value: date } };
                          handleChange(changes);  
                        }}
                        onBlur={handleBlur}
                        showMonthYearDropdown
                        scrollableMonthYearDropdown
                        minDate={new Date("06/01/2021")}
                        maxDate={presentDate}
                        todayButton="Today"
                        disabled={(Number(interval) !== specificIntId)}
                      />
                      {touched.fromDate && errors.fromDate ? <div className={styles.formErrorMsg}>{errors.fromDate}</div> : null}
                    </Form.Group>
                  </Col>
                  <Col lg md={12} sm={12}>
                    <Form.Group controlId="paramToDate">
                      <Form.Label> <Calendar4Week size={15} /> {" "} To:</Form.Label>
                      <ReactDatePicker
                        autoComplete="off"
                        className={touched.toDate && errors.toDate ? "form-control border border-danger" : "form-control"}
                        name="toDate"
                        dateFormat="dd-MMMM-yyyy"
                        dateFormatCalendar="MMMM yyyy"
                        placeholderText={formatPresentDate}
                        selected={values.toDate}
                        value={values.toDate}
                        onChange={ (dt) => {
                          setFieldTouched("toDate", true, true);
                          let date = dt;
                          if(date === null || date === undefined){
                            date = ""
                          }
                          let changes = { target: { name: "toDate", value: date } };
                          handleChange(changes);  
                        }}
                        onBlur={handleBlur}
                        showMonthYearDropdown
                        scrollableMonthYearDropdown
                        minDate={new Date("06/01/2021")}
                        maxDate={presentDate}
                        todayButton="Today"
                        disabled={(Number(interval) !== specificIntId)}
                      />
                      {touched.toDate && errors.toDate ? <div className={styles.formErrorMsg}>{errors.toDate}</div> : null}
                    </Form.Group>
                  </Col>
                </Form.Row>
                {/* NOTE: User and Grp will be added in when user roles are implemented. */}
                {/* <Form.Row> */}
                  {/* <Col lg={4} md={12} sm={12}>
                    <Form.Group controlId="paramUsers">
                      <Form.Label> Users: </Form.Label>
                      <Form.Control 
                        as="select"
                        name="users"
                        onChange={handleChange} 
                        onBlur={handleBlur} 
                        value={values.users}
                        className="form-select my-1 mr-sm-2"
                        custom 
                      >
                        <option className={styles.options} value="" hidden> --Select-- </option>
                        <option className={styles.options} value="Agency User"> Agency User </option>
                      </Form.Control> */}
                    {/* {touched.users && errors.users ? <div className={styles.formErrorMsg}>{errors.users}</div> : null} */}
                    {/* </Form.Group>
                  </Col>
                  <Col lg={4} md={12} sm={12}>
                    <Form.Group controlId="paramGroup">
                      <Form.Label> Group: </Form.Label>
                      <Form.Control 
                        as="select"
                        name="grp"
                        onChange={handleChange} 
                        onBlur={handleBlur} 
                        value={values.grp}
                        className="form-select my-1 mr-sm-2"
                        custom 
                      >
                        <option className={styles.options} value="" hidden> --Select--</option>
                        <option className={styles.options} value="grp1"> Group 1 </option>
                        <option className={styles.options} value="grp2"> Group 2 </option>
                      </Form.Control>
                    {touched.grp && errors.grp ? <div className={styles.formErrorMsg}>{errors.grp}</div> : null}
                    </Form.Group>
                  </Col> */}
                {/* </Form.Row> */}
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  { listLoading ? <Spinner animation="border" /> : <Search/> } {" "}
                  Generate Report
                </Button>{" "}
                <Button onClick={()=> {handleRequestReportReset(); handleReset()}} variant="secondary">
                  Reset
                </Button>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Collapse>
    </Card>
  );
};
